import type { TFunction } from 'i18next';
import type { HighnoteTransactionEvent, Paidol } from 'API';
import { isReceiptRequired } from 'util/receiptUtils';
import {
  AGAVE_FIELD_MAP,
  CODING_TYPE_BY_COLUMN,
  FLEX_FIELD_MAP,
  getCodingSystemType,
  getColumnLabel,
  isColumnRequired,
  normalizeStoredCodingType,
  type CodingColumnId,
} from 'app/util/columnVisibility';

// SEE-1842: what stops a Submit (New -> Needs review) or a Resubmit. The CDK update_transaction
// gate applies the same rule, and both test suites encode the cases in
// docs/specs/SEE-1842-1843-submit-gate-self-approval/design.md, section 2.

/** The coding columns Submit checks, in table order. Not accountingDate: it is admin-only, so a
 *  cardholder could never fill it. Not billableItem or jobCode: nothing requires them. */
const SUBMIT_CODING_COLUMNS: CodingColumnId[] = [
  'glCode',
  'project',
  'costCode',
  'costType',
  'vendor',
  'subJob',
  'location',
  'department',
  'employee',
  'subaccount',
  'equipment',
  'emCostCode',
  'emCostType',
];

export interface SubmitBlockers {
  receipt: boolean;
  note: boolean;
  /** The missing Required coding columns to name, in table order. */
  coding: string[];
}

export interface SubmitBlockerOptions {
  /** A receipt the row's own fields don't show yet: linked receipts, or a file staged on mobile. */
  hasAttachedReceipt?: boolean;
  /** The coding as entered, by column, read instead of the row's coding fields (mobile Save). */
  coding?: Partial<Record<CodingColumnId, unknown>>;
}

const isNoteMissing = (transaction: HighnoteTransactionEvent, paidol: Paidol | null | undefined): boolean =>
  !!paidol?.notesSettings?.isEnabled && !transaction.note?.trim() && transaction.approveWithoutNotes !== true;

const isReceiptMissing = (
  transaction: HighnoteTransactionEvent,
  paidol: Paidol | null | undefined,
  hasAttachedReceipt: boolean
): boolean => {
  const hasReceipt =
    transaction.hasReceipt === true ||
    !!transaction.receiptPath ||
    !!transaction.receiptId ||
    hasAttachedReceipt;
  return isReceiptRequired(transaction, paidol) && !hasReceipt && transaction.approveWithoutReceipts !== true;
};

const missingCoding = (
  transaction: HighnoteTransactionEvent,
  paidol: Paidol | null | undefined,
  coding: SubmitBlockerOptions['coding']
): string[] => {
  const codingSystem = getCodingSystemType(paidol);
  const config = paidol?.codingColumnsConfig;
  // Job code accounts have no Required columns, and a missing isEnabled counts as on.
  if (!codingSystem || codingSystem === 'jobCode' || !config || config.isEnabled === false) return [];

  const fieldMap = codingSystem === 'agave' ? AGAVE_FIELD_MAP : FLEX_FIELD_MAP;
  const fields: Record<string, unknown> = transaction;
  // The id or the hydrated object: a list row can carry either one without the other.
  const isCoded = (column: CodingColumnId, field: string) =>
    coding ? !!coding[column] : !!fields[`${field}Id`] || !!fields[field];
  // Only the user's explicit pick types a row; coding a value never does.
  const rowType = normalizeStoredCodingType(transaction.codingType);
  const missing = SUBMIT_CODING_COLUMNS.filter((column) => {
    const field = fieldMap[column];
    const columnType = CODING_TYPE_BY_COLUMN[column];
    // On a typed row another type's columns are greyed out, so they can't be asked for.
    const isOtherType = !!rowType && !!columnType && columnType !== rowType;
    return !!field && isColumnRequired(column, config) && !isOtherType && !isCoded(column, field);
  });

  const label = (column: CodingColumnId) => getColumnLabel(column, config);
  if (codingSystem === 'agave' && !rowType && isColumnRequired('transactionType', config)) {
    // The per-type columns are asked for once a type is picked. Trimming them never unblocks
    // the row: Transaction type itself is still named.
    return [
      label('transactionType'),
      ...missing.filter((column) => !CODING_TYPE_BY_COLUMN[column]).map(label),
    ];
  }
  return missing.map(label);
};

/** A split line, as Submit all splits turns it into its own transaction (SEE-1604). */
export interface SplitLineForSubmit {
  amountCents: number;
  note: string;
  /** The line's coding picks by transaction field (agaveGlCode, flexCode, ...). */
  coding: object;
  receipts: ReadonlyArray<{ document?: string | null }>;
}

const SPLIT_CODING_FIELDS = SUBMIT_CODING_COLUMNS.flatMap((column) =>
  [AGAVE_FIELD_MAP[column], FLEX_FIELD_MAP[column]].filter((field): field is string => !!field)
);

/**
 * The transaction Submit all splits makes of one line, as the CDK builds it: the parent row with
 * the line's amount, note, coding and first receipt, and none of the parent's coding or receipts.
 */
export const splitLineTransaction = (
  parent: HighnoteTransactionEvent,
  line: SplitLineForSubmit
): HighnoteTransactionEvent => {
  const picks = line.coding as Record<string, unknown>;
  const idOf = (pick: unknown) =>
    typeof pick === 'string' ? pick : (pick as { id?: string } | null | undefined)?.id ?? null;
  const coding = Object.fromEntries(
    SPLIT_CODING_FIELDS.flatMap((field) => [
      [field, picks[field] ?? null],
      [`${field}Id`, idOf(picks[field])],
    ])
  );
  return {
    ...parent,
    ...coding,
    note: line.note,
    approvedAmount: { ...parent.approvedAmount, value: line.amountCents },
    approvedAmountValue: line.amountCents,
    hasReceipt: false,
    receiptPath: line.receipts.find((receipt) => !!receipt.document)?.document ?? null,
    receiptId: null,
  } as HighnoteTransactionEvent;
};

/** What the company requires that the transaction is missing (spec section 2). */
export const getSubmitBlockers = (
  transaction: HighnoteTransactionEvent,
  paidol: Paidol | null | undefined,
  { hasAttachedReceipt = false, coding }: SubmitBlockerOptions = {}
): SubmitBlockers => ({
  receipt: isReceiptMissing(transaction, paidol, hasAttachedReceipt),
  note: isNoteMissing(transaction, paidol),
  coding: missingCoding(transaction, paidol, coding),
});

export const isSubmitBlocked = ({ receipt, note, coding }: SubmitBlockers): boolean =>
  receipt || note || coding.length > 0;

/** "Receipt, Note, GL Code": what is missing, in the order the message names it. */
export const submitBlockerItems = ({ receipt, note, coding }: SubmitBlockers, t: TFunction): string =>
  [receipt && t('submitGate.receipt'), note && t('submitGate.note'), ...coding].filter(Boolean).join(', ');

/** "Missing before submitting: Receipt, Note, GL Code", the same text the server refuses with. */
export const describeSubmitBlockers = (blockers: SubmitBlockers, t: TFunction): string =>
  t('submitGate.missing', { items: submitBlockerItems(blockers, t) });

/** Whose Submit the rule applies to. An admin acting on someone else's transaction is never
 *  blocked; a group admin submitting their own gets the same check as a cardholder. */
export const isSubmitGatedFor = ({
  isOwner,
  isCardholder,
  canReviewRow,
}: {
  isOwner: boolean;
  isCardholder: boolean;
  canReviewRow: boolean;
}): boolean => isOwner || isCardholder || !canReviewRow;
